import {Component, Input, Output, EventEmitter, ViewChild} from '@angular/core';

import {getPropertySchema, getSchemaProperties, getSchemaFromValue, getDefaultValueForType, range, SCHEMA_TYPES} from './util';

@Component({
    selector: 'json-schema-form',
    styles: [`
      .nested-editor {
        padding-left: 15px;
      }
      .type-choice {
        display: inline;
      }
      .dropdown .btn {
        min-width: 100%;
      }
    `],
    template: `
<div class="form-group form-group-sm" *ngIf="schema">
  <schema-label
      #labelComponent
      [label]="label"
      [schema]="schema"
      [hidden]="schema.hidden"
      [refBase]="refBase"
      [expand]="value !== undefined || depth > 0"
      [required]="required"
      [showExpand]="true"
      [showRemove]="showRemove"
      (onRemoved)="valueChange.emit(undefined)"
      (expandChange)="setDefaultValue()"
      (schemaChange)="setSchema($event)"
      (typeChange)="setDefaultValue(); setParameter()"
      (subtypeChange)="setSubschema($event)">
  </schema-label>
  <div *ngIf="labelComponent.expand">
    <div *ngIf="labelComponent.type === 'string' || labelComponent.type === 'number' || labelComponent.type === 'integer' || labelComponent.type === 'boolean'">
      <parameter *ngIf="parameter" [parameter]="parameter"
            [model]="parameterModel"
            [readOnly]="readOnly"
            [hideLabel]="true" [view]="'console'"
            [fillFromSecrets]="schema.fromSecrets"
            (valueChange)="value = $event; valueChange.emit(value)"></parameter>
    </div>
    <div *ngIf="value !== undefined">
      <div class="nested-editor" *ngIf="labelComponent.type === 'object'">
        <json-schema-form
               *ngFor="let prop of allProperties"
               [label]="prop"
               [nested]="true"
               [depth]="depth - 1"
               [readOnly]="prop === schema.discriminator"
               [required]="(schema.required || []).indexOf(prop) !== -1"
               [schema]="util.getPropertySchema(prop, schema, refBase) || util.getPropertySchema(prop, subschema, refBase) || util.getSchemaFromValue(value[prop])"
               [showRemove]="removableProperties.indexOf(prop) !== -1"
               [refBase]="refBase"
               [value]="value[prop]"
               (valueChange)="nestedValueChange(prop, $event)">
        </json-schema-form>
        <div class="input-group input-group-sm" *ngIf="schema.additionalProperties || (allProperties.length === removableProperties.length && !schema['x-abstract'])">
          <input class="form-control" placeholder="Add a field" [(ngModel)]="additionalPropertyName" (keyup)="$event.keyCode === 13 ? addProperty() : null">
          <div class="input-group-btn">
            <button class="btn btn-default dropdown-toggle" data-toggle="dropdown">
              <span>{{additionalPropertyType || 'Type'}}</span>
              <i class="fa fa-right fa-caret-down"></i>
            </button>
            <ul class="dropdown-menu">
              <li *ngFor="let schemaType of util.SCHEMA_TYPES" [hidden]="schemaType === 'array'">
                <a (click)="additionalPropertyType = schemaType">{{schemaType}}</a>
              </li>
            </ul>
            <a class="btn btn-default" (click)="addProperty()">Add</a>
          </div>
        </div>
      </div>
      <div class="nested-editor" *ngIf="labelComponent.type === 'array'">
        <json-schema-form
               *ngFor="let val of util.range(numItems || 0); let valIdx = index;"
               [label]="label + '[' + valIdx + ']'"
               [nested]="true"
               [schema]="schema.items"
               [depth]="1"
               [refBase]="refBase"
               [showRemove]="true"
               [value]="value[valIdx]"
               (valueChange)="nestedValueChange(valIdx, $event);">
        </json-schema-form>
        <div class="btn-toolbar">
          <a class="btn btn-sm btn-default" (click)="numItems = numItems + 1">Add item</a>
        </div>
      </div>
    </div>
  </div>
</div>
    `
})
export class JSONSchemaFormComponent {
  @Input() label;
  @Input('schema') inputSchema;
  schema:any;
  subschema:any;
  parameter:any;
  parameterModel:any = {};
  @Input() refBase;
  @Input('value') inputValue;
  value:any;
  @Input() nested:boolean;
  @Input() readOnly:boolean;
  @Input() depth:number;
  @Input() required:boolean;
  @Input() showRemove:boolean;
  @Output() valueChange = new EventEmitter();
  @ViewChild('labelComponent') labelComponent;
  allProperties:string[] = [];
  removableProperties:string[];
  additionalPropertyName:string;
  additionalPropertyType:string;

  numItems = 0;

  util = {range, getSchemaProperties, getPropertySchema, getSchemaFromValue, SCHEMA_TYPES};
  assign = Object.assign;

  ngOnChanges() {
    this.setSchema(this.inputSchema);
    this.readOnly = this.readOnly || this.schema.readOnly;
    if (this.value !== this.inputValue) {
      this.value = this.parameterModel[this.parameter.name] = this.inputValue;
      if (Array.isArray(this.value)) {
        this.numItems = this.value.length;
      }
    }
  }

  ngAfterViewInit() {
    setTimeout(() => this.setDefaultValue() && this.setParameter());
  }

  setParameter() {
    let type = this.labelComponent && this.labelComponent.type;
    this.parameter = Object.assign({name: this.label, type}, this.schema);
  }

  setSchema(schema) {
    this.schema = schema;
    this.setDefaultValue();
    this.setProperties();
    this.setParameter();
  }

  setSubschema(schema) {
    if (this.subschema) {
      for (let key in this.subschema.properties) {
        delete this.value[key];
      }
    }
    this.subschema = schema;
    this.setProperties();
    if (schema && this.schema.discriminator) {
      this.value[this.schema.discriminator] = schema.title;
    }
    this.valueChange.emit(this.value);
  }

  setProperties() {
    if (this.schema['x-abstract'] && !this.subschema) {
      this.allProperties = [];
      this.removableProperties = [];
      return;
    }
    this.allProperties = getSchemaProperties(this.schema || {}, this.refBase, this.value, this.subschema);
    let nonRemovable = getSchemaProperties(this.schema || {}, this.refBase, undefined, this.subschema);
    this.removableProperties = this.allProperties.filter(p => nonRemovable.indexOf(p) === -1);
  }

  setDefaultValue() {
    if (!this.labelComponent) return;
    if (!this.labelComponent.expand) {
      this.value = undefined;
    } else if (this.labelComponent.type === 'object' && (!this.value || typeof this.value !== 'object')) {
      this.value = {};
      this.setProperties();
    } else if (this.labelComponent.type === 'array' && !Array.isArray(this.value)) {
      this.value = [];
    } else {
      return;
    }
    this.valueChange.emit(this.value);
  }

  nestedValueChange(propOrIdx, val) {
    if (val === undefined) {
      if (Array.isArray(this.value)) {
        this.value.splice(propOrIdx, 1);
        this.numItems = this.value.length;
      } else {
        delete this.value[propOrIdx];
      }
    } else {
      this.value[propOrIdx] = val;
    }
    if (!Array.isArray(this.value)) {
      this.setProperties();
    }
    this.valueChange.emit(this.value);
  }

  addProperty() {
    if (!this.additionalPropertyName) return;
    let val = getDefaultValueForType(this.additionalPropertyType || 'string');
    this.nestedValueChange(this.additionalPropertyName, val);
    this.additionalPropertyName = this.additionalPropertyType = null;
  }
}



// WEBPACK FOOTER //
// ./src/app/json-schema-form/json-schema-form.component.ts